import React, { useEffect, useState } from 'react';
import { Button, Input, message, Popconfirm, Modal, Card, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, EditOutlined, ThunderboltOutlined, ExperimentOutlined, LinkOutlined } from '@ant-design/icons';
import { agentConfigApi } from '../services/api-services';
import { useTheme } from '../contexts/ThemeContext';
import { useTestProfile } from '../hooks/useTestProfile';

const PROVIDER_OPTIONS = [
  { label: 'Anthropic', value: 'anthropic' },
  { label: 'OpenAI', value: 'openai' },
  { label: 'OpenRouter', value: 'openrouter' },
  { label: 'AcpKiro', value: 'acp_kiro' },
  { label: 'AcpCopilot', value: 'acp_copilot' },
  { label: 'AcpClaude', value: 'acp_claude' },
];

interface Profile {
  name: string;
  base_url: string;
  provider: string;
  token: string;
  default_model: string;
  models: string[];
  website: string;
}

const EMPTY_PROFILE: Profile = { name: '', base_url: '', provider: 'anthropic', token: '', default_model: '', models: [], website: '' };

const AgentBaseConfig: React.FC = () => {
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'glass';
  const { testProfile } = useTestProfile();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<Profile>({ ...EMPTY_PROFILE });

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await agentConfigApi.getGlobalConfig();
      setProfiles(res.profiles || []);
      setActiveProfile(res.active_profile || '');
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfiles = async (next: Profile[]) => {
    await agentConfigApi.updateGlobalConfig({ profiles: next });
    setProfiles(next);
  };

  const handleApply = async (name: string) => {
    setApplying(name);
    try {
      await agentConfigApi.applyGlobalProfile(name);
      setActiveProfile(name);
      message.success('应用成功');
    } catch {
      message.error('应用失败');
    } finally {
      setApplying('');
    }
  };

  const handleDelete = async (name: string) => {
    const next = profiles.filter((p) => p.name !== name);
    try {
      await saveProfiles(next);
      if (activeProfile === name) {
        setActiveProfile('');
      }
      message.success('已删除');
    } catch {
      message.error('删除失败');
    }
  };

  const openCreate = () => {
    setEditingProfile(null);
    setForm({ ...EMPTY_PROFILE });
    setModalOpen(true);
  };

  const openEdit = (p: Profile) => {
    setEditingProfile(p);
    const models = p.models?.length ? p.models : (p.default_model ? [p.default_model] : []);
    setForm({ ...p, models });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    if (!form.name.trim()) {
      message.warning('请输入配置名称');
      return;
    }
    let next: Profile[];
    const trimmedName = form.name.trim();
    if (editingProfile) {
      if (trimmedName !== editingProfile.name && profiles.some((p) => p.name === trimmedName)) {
        message.warning('配置名称已存在');
        return;
      }
      next = profiles.map((p) => (p.name === editingProfile.name ? { ...form, name: trimmedName } : p));
    } else {
      if (profiles.some((p) => p.name === trimmedName)) {
        message.warning('配置名称已存在');
        return;
      }
      next = [...profiles, { ...form, name: trimmedName }];
    }
    try {
      await saveProfiles(next);
      setModalOpen(false);
      message.success(editingProfile ? '已更新' : '已创建');
      // If editing the active profile, re-apply to sync changes
      if (editingProfile && activeProfile === editingProfile.name) {
        const trimmedName = form.name.trim();
        await agentConfigApi.applyGlobalProfile(trimmedName);
      }
    } catch {
      message.error('保存失败');
    }
  };

  const handleTest = (profile: Profile) => testProfile(profile.name);

  const selectStyle: React.CSSProperties = { width: '100%', padding: '4px 8px', borderRadius: 6, border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d9d9d9'}`, fontSize: 14, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', color: isDark ? 'rgba(255,255,255,0.88)' : undefined };

  const getLabel = (options: { label: string; value: string }[], val: string) =>
    options.find((o) => o.value === val)?.label || val || '-';

  const truncate = (s: string, len: number) => (s && s.length > len ? s.slice(0, len) + '...' : s || '-');

  return (
    <div className="apple-page agent-base-config-page">
      <div className="apple-page-header agent-base-config-header">
        <div>
          <h1 className="apple-page-title">大模型配置</h1>
          <p className="apple-page-subtitle agent-base-config-subtitle">智能体全局配置管理</p>
        </div>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={openCreate}>新建配置</Button>
      </div>

      {loading ? (
        <div className="agent-base-config-state">加载中...</div>
      ) : profiles.length === 0 ? (
        <div className="agent-base-config-state">暂无配置，点击"新建配置"创建</div>
      ) : (
        <div className="agent-base-config-grid">
          {profiles.map((p) => {
            const isActive = p.name === activeProfile;
            return (
              <Card
                key={p.name}
                className={`agent-config-card${isActive ? ' agent-config-card-active' : ''}`}
                title={
                  <div className="agent-config-card-title">
                    <span>{p.name}</span>
                    {isActive && <Tag icon={<CheckCircleOutlined />} color="success">默认</Tag>}
                  </div>
                }
                extra={
                  <Popconfirm title="确认删除该配置？" onConfirm={() => handleDelete(p.name)}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} className="agent-config-card-delete" />
                  </Popconfirm>
                }
                actions={[
                  <Popconfirm
                    key="apply"
                    title="确认应用该配置？"
                    description="应用后将同步更新 Hermes 和 Claude Code 的相关配置"
                    onConfirm={() => handleApply(p.name)}
                    okText="确认应用"
                    cancelText="取消"
                  >
                    <Button
                      type="link"
                      icon={<ThunderboltOutlined />}
                      loading={applying === p.name}
                      disabled={isActive}
                    >
                      应用
                    </Button>
                  </Popconfirm>,
                  <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => openEdit(p)}>编辑</Button>,
                  <Button key="test" type="link" icon={<ExperimentOutlined />} onClick={() => handleTest(p)}>测试</Button>,
                ]}
              >
                <div className="agent-config-card-body">
                  <div className="agent-config-field">
                    <span className="agent-config-field-label">Provider</span>
                    <span className="agent-config-field-value">{getLabel(PROVIDER_OPTIONS, p.provider)}{p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="agent-config-link"><LinkOutlined /></a>}</span>
                  </div>

                  <div className="agent-config-field">
                    <span className="agent-config-field-label">默认模型</span>
                    <span className="agent-config-field-value agent-config-code">
                      {p.default_model || '-'}
                      {p.models?.length > 1 && <span style={{ marginLeft: 6, color: 'var(--apple-text-secondary)', fontSize: 12 }}>+{p.models.length - 1} 个</span>}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        title={editingProfile ? '编辑配置' : '新建配置'}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>配置名称</div>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="例如：生产环境"
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Provider</div>
            <select
              value={form.provider}
              onChange={(e) => setForm({ ...form, provider: e.target.value })}
              style={selectStyle}
            >
              {PROVIDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>官网链接</div>
            <Input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://console.anthropic.com"
            />
          </div>
          {!['acp_kiro', 'acp_copilot', 'acp_claude'].includes(form.provider) && (
            <>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Base URL</div>
                <Input
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder="https://api.anthropic.com"
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Token</div>
                <Input
                  value={form.token}
                  onChange={(e) => setForm({ ...form, token: e.target.value })}
                  placeholder="sk-..."
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </>
          )}
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>模型列表</div>
            {(form.models || []).map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={form.default_model === m}
                  onChange={() => setForm({ ...form, default_model: m })}
                  title="设为默认"
                />
                <Input
                  value={m}
                  onChange={(e) => {
                    const next = [...form.models];
                    next[i] = e.target.value;
                    setForm({ ...form, models: next, default_model: form.default_model === m ? e.target.value : form.default_model });
                  }}
                  placeholder="例如：claude-sonnet-4-20250514"
                  style={{ fontFamily: 'monospace', flex: 1 }}
                />
                {form.default_model === m && <Tag color="blue" style={{ flexShrink: 0 }}>默认</Tag>}
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const next = form.models.filter((_, idx) => idx !== i);
                    setForm({ ...form, models: next, default_model: form.default_model === m ? (next[0] || '') : form.default_model });
                  }}
                />
              </div>
            ))}
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setForm({ ...form, models: [...(form.models || []), ''] })}
            >
              添加模型
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentBaseConfig;
